import { useSearch } from '@src/entities/RiskRecord/ui/hooks/useSearch';
import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { Details } from '@src/pages/private/Analysis/EidSearch/components/Details';
import { Form } from '@src/pages/private/Analysis/EidSearch/components/Form';
import { widgets } from '@src/pages/private/Analysis/EidSearch/includes/configuration';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { DashboardLayout } from '@src/shared/ui/layouts/Dashboard';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { isEmpty, isUndefined } from 'lodash';
import React, { useEffect, useMemo } from 'react';

/**
 * Page: EidSearch
 *
 * Find additional information about a specific Risk Record.
 *
 * @returns {Element}
 * @constructor
 */
export function EidSearch() {
  const router = useRouter();
  const routeParams = router.getRouteParams();
  const eid = !isUndefined(routeParams?.eid) && !isEmpty(routeParams?.eid) ? routeParams?.eid : '';

  const { hasPermission } = useCurrentUser();

  const { assets, isLoadingAssets } = useAssets([
    {
      apiKey: 'RiskRecord',
      queryKey: 'ListRiskCodes',
    },
    {
      apiKey: 'Tag',
      queryKey: 'ListTags',
    },
  ]);

  const { handleSearch, isSearching, searchResults } = useSearch();

  const riskRecord = searchResults?.GetRiskRecord?.data || {};
  const ireRiskRecord = searchResults?.GetIreRiskRecord?.data || {};

  const hasResults = !isEmpty(searchResults);

  /**
   * If an EID is passed in the URL, fetch the risk record.
   */
  useEffect(() => {
    if (
      !isEmpty(eid) &&
      !isSearching &&
      (eid !== riskRecord?.eid || eid !== ireRiskRecord?.riskReports?.[0]?.riskReportDetail?.enterpriseId)
    ) {
      handleSearch({ eid });
    }
  }, [eid, handleSearch, isSearching]);

  /**
   * check if the EID has a DOB attribute
   */
  const hasDOB = useMemo(() => {
    return riskRecord?.ireRiskRecord?.remediationFindings?.find((attribute) => {
      return attribute.attributeType === 'dob';
    });
  }, [riskRecord?.ireRiskRecord?.remediationFindings]);

  /**
   * check if the EID has an SSN attribute
   */
  const hasSSN = useMemo(() => {
    return riskRecord?.ireRiskRecord?.remediationFindings?.find((attribute) => {
      return attribute.attributeType === 'ssn';
    });
  }, [riskRecord?.ireRiskRecord?.remediationFindings]);

  /**
   * add icon component to each widget
   *
   * @type {any[]}
   */
  const theWidgets = useMemo(() => {
    return Object.values(widgets)
      .sort((a, b) => {
        return a.order - b.order;
      })
      .map((widget) => {
        const theWidget = { ...widget };

        if (theWidget.accessor === 'attribute-summary') {
          theWidget.panel = true;
        }

        if (['ire-record-detail', 'ire-related-entities'].includes(theWidget.accessor)) {
          const entityId = ireRiskRecord?.riskReports?.[0]?.g2EntityId;
          if (!isUndefined(entityId)) {
            theWidget.description = (
              <React.Fragment>
                Entity ID:{' '}
                <Link
                  css={{ fontWeight: 'normal' }}
                  href={`https://ire-portal.optum.com/entityResume?entityId=${entityId}`}
                >
                  {entityId}
                </Link>
              </React.Fragment>
            );
          }
        }

        theWidget.icon = <IconSymbol color="#000000" icon={widget?.icon} variant="outlined" />;

        const Component = widgets?.[theWidget?.accessor]?.component;

        theWidget.element = Component ? (
          <Component
            assets={assets}
            handleSearch={handleSearch}
            ireRiskRecord={ireRiskRecord}
            options={theWidget}
            riskRecord={riskRecord}
          />
        ) : null;

        return theWidget;
      })
      .filter((widget) => {
        // Must have IRE Access
        if (['ire-record-detail', 'ire-related-entities'].includes(widget.accessor)) {
          return hasPermission('IRE', 'access');
        }

        return true;
      });
  }, [widgets, hasSSN, hasDOB, riskRecord, assets, ireRiskRecord]);

  return (
    <ErrorHandler location="src/pages/private/Analysis/EidSearch/EidSearch.jsx">
      <DashboardLayout
        hasData={hasResults && !isEmpty(eid)}
        header={{
          LeftComponent: <Form isFetching={isSearching} isLoading={isLoadingAssets} />,
          RightComponent: (
            <Details
              actionPaths={riskRecord?.recordActionPaths}
              activeInfo={riskRecord?.activeInfo}
              associatedEids={riskRecord?.associatedEID}
              eid={eid}
              handleSearch={handleSearch}
              ireRiskRecord={ireRiskRecord}
              note={riskRecord?.note}
              riskCodes={assets?.ListRiskCodes?.data}
              riskRecord={riskRecord}
              tags={riskRecord?.tags}
            />
          ),
        }}
        isFetching={isSearching}
        isLoading={isLoadingAssets}
        title="EID Search"
        widgets={theWidgets}
      />
      {!isEmpty(eid) && !hasResults && !isSearching && (
        <Alert title="Not Found" variant="info">
          The EID you are searching for could not be found.
        </Alert>
      )}
    </ErrorHandler>
  );
}
